import { useQueryClient } from 'react-query';
import { IProgram } from 'shared/interfaces/IProgram';
import { useProgramService } from 'shared/services/programService';

/**
 * Program Type lives on the program, not on the automation setup. This loads the selected program
 * (for its current type) and writes a changed type back with /program/update.
 */
export const useSelectedProgramType = (programId?: string | null) => {
  const queryClient = useQueryClient();
  const { getSingleTvProgramList, updateTvProgramList } = useProgramService();
  const { data, isLoading } = getSingleTvProgramList(programId || undefined);
  const { updateMutation } = updateTvProgramList();

  // /program/show may return `data` as a list or as the single record.
  const records: IProgram[] = Array.isArray(data) ? data : data ? [data as unknown as IProgram] : [];
  const program = programId ? records.find((record) => record?._id === programId) : undefined;

  // Resolves without a request when the type is unchanged; rejects with the API's message on failure.
  const saveProgramType = async (programType?: string | null, updatedBy?: string) => {
    if (!program || !programType || programType === program.program_type) return;

    const response = await updateMutation.mutateAsync({
      data: {
        _id: program._id,
        program_name: program.program_name,
        program_description: program.program_description,
        channel_id: program.publication_id || program.channel_id,
        publication_id: program.publication_id,
        program_type: programType,
        updated_by: updatedBy as string,
      },
    });
    if (response?.success === false) throw new Error(response.server_response || 'Failed to update Program Type');

    queryClient.invalidateQueries('program');
    queryClient.invalidateQueries('single-program');
  };

  return { program, loading: isLoading, saveProgramType, saving: updateMutation.isLoading };
};
